import type { AuthUser, FilesQueryParams, FilesResponse, SasTokenResponse } from '@/types';

// import type { AuthUser, SasTokenResponse, FilesQueryParams, FilesResponse, UploadRecord } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function apiFetch<T>(
  path: string,
  options?: RequestInit & { noRedirect?: boolean },
): Promise<T> {
  const { noRedirect, ...fetchOptions } = options || {};

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...fetchOptions,
  });

  if (res.status === 401 && !noRedirect) {
    window.location.href = `${API_BASE}/auth/login`;
    throw new Error('Not authenticated');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error: ${res.status}`);
  }

  return res.json();
}

// ─── Auth (BFF) ──────────────────────────────────────────────────────────────

export function signIn(): void {
  // Just redirect — the server handles the entire OAuth flow
  window.location.href = `${API_BASE}/auth/login`;
}

export async function getMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>('/auth/me', { noRedirect: true });
}

export async function signOut(): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    // No Content-Type header, no body needed
  });

  if (!res.ok) throw new Error('Logout failed');

  const { logoutUrl } = await res.json();
  window.location.href = logoutUrl;
}

// ─── Data Catalog (Data API, through the BFF proxy) ─────────────────────────

/** OpenAPI document of the Data API's catalog endpoints, served by the BFF at /api/data. */
export function dataCatalogOpenApiUrl(): string {
  return `${API_BASE}/api/data/openapi.json`;
}

// ─── SAS token ───────────────────────────────────────────────────────────────

export async function getSasToken(
  product: string,
  entity: string,
  classification: string,
  fileName: string,
  displayName?: string,
): Promise<SasTokenResponse> {
  const params = new URLSearchParams({ product, entity, classification, fileName });
  if (displayName) params.set('displayName', displayName);
  return apiFetch<SasTokenResponse>(`/api/upload/sas-token?${params}`);
}

// ─── Upload complete ─────────────────────────────────────────────────────────

export async function completeUpload(
  uploadId: string,
  sizeBytes: number,
  blobVersionId?: string,
  rowCount?: number | null,
): Promise<{ uploadId: string; status: string }> {
  return apiFetch(`/api/upload/${uploadId}/complete`, {
    method: 'POST',
    body: JSON.stringify({
      sizeBytes,
      blobVersionId,
      // Only include rowCount if it's a real number — omit null/undefined
      ...(typeof rowCount === 'number' ? { rowCount } : {}),
    }),
  });
}

// ─── File archive ────────────────────────────────────────────────────────────

export async function fetchFiles(params: FilesQueryParams): Promise<FilesResponse> {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.product) search.set('product', params.product);
  if (params.status) search.set('status', params.status);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));

  return apiFetch<FilesResponse>(`/api/files?${search}`);
}

// ─── Admin ───────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  user_sub: string;
  email: string;
  name: string | null;
  tenant: 'inter_ikea' | 'other';
  role: 'uploader' | 'admin';
  product_scope: string[];
  active: boolean;
  added_by: string | null;
  added_at: string;
  last_login: string | null;
}

export async function listAdminUsers(): Promise<{ users: AdminUser[] }> {
  return apiFetch('/api/admin/users');
}

export async function updateAdminUser(
  id: string,
  updates: { role?: 'uploader' | 'admin'; product_scope?: string[]; active?: boolean },
): Promise<{ user: AdminUser }> {
  return apiFetch(`/api/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

// ─── Direct blob upload (browser → Azure Storage) ────────────────────────────

export async function uploadToBlob(sasUrl: string, data: Blob, contentType: string): Promise<void> {
  const res = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: data,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Blob upload failed (${res.status}): ${text.slice(0, 200)}`);
  }
}

// ─── Catalog (admin) ─────────────────────────────────────────────────────────

export interface CatalogEntityAdmin {
  id: string;
  product_id: string;
  name: string;
  description: string | null;
  ordering: number;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface CatalogProductAdmin {
  id: string;
  name: string;
  description: string | null;
  ordering: number;
  active: boolean;
  special_route: string | null;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
  entities: CatalogEntityAdmin[];
}

export interface CatalogClassificationAdmin {
  id: string;
  name: string;
  description: string | null;
  ordering: number;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export async function listAdminProducts(): Promise<{ products: CatalogProductAdmin[] }> {
  return apiFetch('/api/admin/catalog/products');
}

export async function createAdminProduct(input: {
  id: string;
  name: string;
  description?: string | null;
}): Promise<{ product: CatalogProductAdmin }> {
  return apiFetch('/api/admin/catalog/products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updateAdminProduct(
  id: string,
  updates: { name?: string; description?: string | null; ordering?: number; active?: boolean },
): Promise<{ product: CatalogProductAdmin }> {
  return apiFetch(`/api/admin/catalog/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function createAdminEntity(
  productId: string,
  input: { id: string; name: string; description?: string | null },
): Promise<{ entity: CatalogEntityAdmin }> {
  return apiFetch(`/api/admin/catalog/products/${encodeURIComponent(productId)}/entities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updateAdminEntity(
  productId: string,
  entityId: string,
  updates: { name?: string; description?: string | null; ordering?: number; active?: boolean },
): Promise<{ entity: CatalogEntityAdmin }> {
  return apiFetch(
    `/api/admin/catalog/products/${encodeURIComponent(productId)}/entities/${encodeURIComponent(entityId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    },
  );
}

export async function listAdminClassifications(): Promise<{
  classifications: CatalogClassificationAdmin[];
}> {
  return apiFetch('/api/admin/catalog/classifications');
}

export async function createAdminClassification(input: {
  id: string;
  name: string;
  description?: string | null;
}): Promise<{ classification: CatalogClassificationAdmin }> {
  return apiFetch('/api/admin/catalog/classifications', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updateAdminClassification(
  id: string,
  updates: { name?: string; description?: string | null; ordering?: number; active?: boolean },
): Promise<{ classification: CatalogClassificationAdmin }> {
  return apiFetch(`/api/admin/catalog/classifications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

// ─── Notification recipients (admin) ─────────────────────────────────────────

export interface NotificationRecipient {
  id: number;
  email: string;
  name: string | null;
  added_by: string;
  added_at: string;
  updated_by: string | null;
  updated_at: string | null;
  active: boolean;
}

export async function listNotificationRecipients(): Promise<{
  recipients: NotificationRecipient[];
}> {
  return apiFetch('/api/admin/notifications');
}

export async function createNotificationRecipient(data: {
  email: string;
  name?: string | null;
}): Promise<{ recipient: NotificationRecipient }> {
  return apiFetch('/api/admin/notifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNotificationRecipient(
  id: number,
  data: { name?: string | null; active?: boolean },
): Promise<{ recipient: NotificationRecipient }> {
  return apiFetch(`/api/admin/notifications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
